import React, { useCallback, useEffect, useRef, useState } from 'react';
import type { JotDocDataType } from '@components/Markdown/JotDocViewer';
import {
  closestCenter,
  DndContext,
  type DragEndEvent,
  DragOverlay,
  type DragStartEvent,
  MeasuringStrategy,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { Box, CircularProgress, Typography } from '@mui/material';
import { useAppDispatch } from '@store/hooks';

import { indicatorRegistry } from '../../../services/indicatorRegistry';
import { NoteTelemetryService, trackNoteTelemetry } from '../../../utils/analytics/noteTelemetry';
import { useJotdocSections } from '../hooks/useJotdocSections';
import { jotdocsApi } from '../store/api';
import { useJotDocSectionSave, useJotDocSectionSaveWithOptimistic } from '../utils/jotdoc-save';
import {
  insertSectionAfter,
  parseMarkdownH2Sections,
  removeSectionFromMarkdown,
  reorderSectionsInMarkdown,
  updateSectionHeading,
} from '../utils/markdown-utils';

import JotDocSection from './jot-doc-section';

interface JotDocNoteSectionViewProps {
  jotdoc: JotDocDataType | null;
  loading?: boolean;
  encounterId?: string;
  onEditorsReady?: (editorsMap: Map<string, any>) => void;
  selectedSectionIds?: string[];
  onToggleSectionSelection?: (sectionId: string) => void;
  isScrolling?: boolean;
  troubleWords?: Array<{ correct_spelling: string; misspellings: string[] }>;
  misspelledWords?: string[];
  onWordClick?: (word: string) => void;
  streamingIndicator?: React.ReactNode;
}

const JotDocNoteSectionView: React.FC<JotDocNoteSectionViewProps> = ({
  jotdoc,
  loading = false,
  encounterId,
  onEditorsReady,
  selectedSectionIds = [],
  onToggleSectionSelection,
  isScrolling = false,
  troubleWords,
  misspelledWords,
  onWordClick,
  streamingIndicator,
}) => {
  // Drag and drop state for section reordering
  const [activeSectionId, setActiveSectionId] = useState<string | null>(null);
  const [validDrop, setValidDrop] = useState(false);

  // Track current section content (for detecting changes before save)
  const sectionContentRef = useRef<Map<string, string>>(new Map());

  // Track BlockNote editor instances by section ID
  const blockNoteEditorsRef = useRef<Map<string, any>>(new Map());

  // Telemetry refs
  const docVersionRef = useRef<number>(0);
  const lastJotDocViewedIdRef = useRef<string | null>(null);
  const lastContentRef = useRef<string>(jotdoc?.content || '');

  // Track document version for analytics (increments when jotdoc.content changes)
  useEffect(() => {
    if (jotdoc?.content && lastContentRef.current !== jotdoc.content) {
      docVersionRef.current += 1;
      lastContentRef.current = jotdoc.content;
    }
  }, [jotdoc?.content]);

  // Analytics configuration
  const analyticsDocId = jotdoc ? `jotdoc:${jotdoc.jotdoc_id}` : 'jotdoc:unknown';
  const getDocVersion = useCallback(() => docVersionRef.current.toString(), []);

  // Set up encounter context
  useEffect(() => {
    if (encounterId) {
      NoteTelemetryService.setEncounterId(encounterId);
    }
  }, [encounterId]);

  // Cleanup telemetry service on unmount
  useEffect(() => {
    return () => {
      NoteTelemetryService.dispose();
    };
  }, []);

  // Track note_viewed event when jotdoc is first loaded
  useEffect(() => {
    if (jotdoc && lastJotDocViewedIdRef.current !== jotdoc.jotdoc_id) {
      lastJotDocViewedIdRef.current = jotdoc.jotdoc_id;
      trackNoteTelemetry('note_viewed', {
        docId: analyticsDocId,
        docVersion: getDocVersion(),
        metadata: {
          note_id: jotdoc.jotdoc_id,
          encounter_id: encounterId,
        },
      });
    }
  }, [jotdoc?.jotdoc_id, analyticsDocId, getDocVersion, encounterId]);

  // Save hook
  const { saveSection, saveMarkdown, isSaving } = useJotDocSectionSave();

  // Dispatch for optimistic cache updates
  const dispatch = useAppDispatch();

  // Parse markdown content into sections by h2 headings
  const { sections } = useJotdocSections(jotdoc?.content);

  useEffect(() => {
    sections.forEach(section => {
      if (!sectionContentRef.current.has(section.id)) {
        sectionContentRef.current.set(section.id, section.text);
      }
    });
  }, [sections]);

  // Reset section content map only when document ID changes (switching documents)
  // Don't clear on content changes - that would cause unnecessary re-renders after saves
  React.useEffect(() => {
    if (jotdoc?.jotdoc_id) {
      sectionContentRef.current.clear();
      blockNoteEditorsRef.current.clear();
    }
  }, [jotdoc?.jotdoc_id]);

  // Expose editors map to parent component whenever it changes
  React.useEffect(() => {
    if (onEditorsReady) {
      // Create a new map reference to trigger updates
      const currentMap = new Map(blockNoteEditorsRef.current);
      onEditorsReady(currentMap);
    }
  }, [onEditorsReady, sections]); // Re-run when sections change

  // Configure drag sensors for section reordering (8px activation distance)
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
  );

  // Handle section drag start
  const handleSectionDragStart = useCallback((event: DragStartEvent) => {
    const sectionId = event.active.id as string;
    setActiveSectionId(sectionId);
  }, []);

  // Handle section drag end
  const handleSectionDragEnd = useCallback(
    async (event: DragEndEvent) => {
      const { active, over } = event;

      // Determine if this was a valid drop
      const wasValidDrop = !!(over && active.id !== over.id && jotdoc?.jotdoc_id);

      // Set validDrop state before clearing (DragOverlay reads this)
      setValidDrop(wasValidDrop);

      // Clear drag state
      setActiveSectionId(null);

      if (!wasValidDrop || !jotdoc?.jotdoc_id) {
        return;
      }

      // Get current section order
      const currentOrder = sections.map(s => s.id);

      // Calculate new order
      const oldIndex = currentOrder.indexOf(active.id as string);
      const newIndex = currentOrder.indexOf(over.id as string);

      if (oldIndex === -1 || newIndex === -1) {
        return;
      }

      // Create new order array
      const newSectionOrder = [...currentOrder];
      const [movedItem] = newSectionOrder.splice(oldIndex, 1);
      newSectionOrder.splice(newIndex, 0, movedItem);

      // Use current content from cache (source of truth) via ref
      const currentMarkdown = currentMarkdownRef.current;
      if (!currentMarkdown) {
        return;
      }

      // Reconstruct markdown with new section order
      const reorderedMarkdown = reorderSectionsInMarkdown(currentMarkdown, newSectionOrder);

      // Only save if order actually changed
      if (reorderedMarkdown === currentMarkdown) {
        return;
      }

      console.log('[JotDoc] Reordering sections', {
        jotdocId: jotdoc.jotdoc_id,
        oldOrder: currentOrder,
        newOrder: newSectionOrder,
        fromIndex: oldIndex,
        toIndex: newIndex,
      });

      // Optimistically update cache immediately for instant UI feedback
      const patchResult = dispatch(
        jotdocsApi.util.updateQueryData(
          'getV2ProviderJotdocsByJotdocId',
          { jotdocId: jotdoc.jotdoc_id },
          draft => {
            if (draft.jotdoc) {
              draft.jotdoc.content = reorderedMarkdown;
            }
          },
        ),
      ) as unknown as { undo: () => void };

      // Save the reordered markdown (mutation's optimistic update will be a no-op since cache is already updated)
      await saveMarkdown({
        jotdocId: jotdoc.jotdoc_id,
        updatedMarkdown: reorderedMarkdown,
        onError: () => {
          // Rollback optimistic update on error
          patchResult.undo();
        },
      });

      // Recalculate indicator positions based on new section order
      const sectionOrderMap = new Map<string, number>();
      newSectionOrder.forEach((sectionId, index) => {
        sectionOrderMap.set(sectionId, index);
      });
      indicatorRegistry.recalculatePositions(sectionOrderMap);
    },
    [jotdoc?.jotdoc_id, sections, saveMarkdown, dispatch],
  );

  // Loading state
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  // Error/empty state
  if (!jotdoc) {
    return (
      <Box sx={{ p: 2, textAlign: 'center' }}>
        <Typography variant="body1" color="text.secondary">
          No document content available
        </Typography>
      </Box>
    );
  }

  // Memoize callbacks per section to prevent remounting
  const sectionCallbacksRef = useRef<
    Map<
      string,
      {
        onMarkdownChange: (markdown: string) => void;
        onBlur: () => void;
        onDelete: () => void;
        onHeadingChange: (newHeading: string) => void;
        onAddSectionBelow: () => void;
      }
    >
  >(new Map());

  // Store latest values in refs so callbacks always have current values
  const jotdocIdRef = useRef<string | undefined>(jotdoc?.jotdoc_id);
  const currentMarkdownRef = useRef<string | undefined>(jotdoc?.content);
  const saveSectionRef = useRef(saveSection);
  const saveMarkdownRef = useRef(saveMarkdown);

  React.useEffect(() => {
    jotdocIdRef.current = jotdoc?.jotdoc_id;
    const previousContent = currentMarkdownRef.current;
    currentMarkdownRef.current = jotdoc?.content;

    // If content changed externally (not from user edits), clear sectionContentRef
    // so that sections use the new parsed content instead of stale tracked content
    if (previousContent !== undefined && previousContent !== jotdoc?.content && jotdoc?.content) {
      const newSections = parseMarkdownH2Sections(jotdoc.content);

      // Check if any tracked content doesn't match the new parsed content
      // If so, clear the ref to use the new parsed content
      for (const section of newSections) {
        const trackedContent = sectionContentRef.current.get(section.id);
        if (trackedContent !== undefined && trackedContent !== section.text) {
          // Content changed externally, clear the tracked content so it uses the new parsed content
          sectionContentRef.current.delete(section.id);
        }
      }
    }
  }, [jotdoc?.jotdoc_id, jotdoc?.content]);

  React.useEffect(() => {
    saveSectionRef.current = saveSection;
  }, [saveSection]);

  React.useEffect(() => {
    saveMarkdownRef.current = saveMarkdown;
  }, [saveMarkdown]);

  // Hook for optimistic section saves (must be after refs are initialized)
  const { saveSectionWithOptimistic } = useJotDocSectionSaveWithOptimistic({
    jotdocId: jotdocIdRef,
    currentMarkdown: currentMarkdownRef,
    sectionContentRef,
    dispatch,
    saveSection,
  });

  // Get or create stable callbacks for a section
  const getSectionCallbacks = useCallback(
    (sectionId: string) => {
      if (!sectionCallbacksRef.current.has(sectionId)) {
        sectionCallbacksRef.current.set(sectionId, {
          onMarkdownChange: (markdown: string) => {
            // Update the section content in our ref
            sectionContentRef.current.set(sectionId, markdown);
          },
          onBlur: async () => {
            console.log('[JotDoc] handleSectionBlur called', { sectionId });

            const currentJotdocId = jotdocIdRef.current;
            // Get current markdown from cache (source of truth) via ref
            const currentMarkdown = currentMarkdownRef.current;
            if (!currentJotdocId || !currentMarkdown) {
              console.log('[JotDoc] Blur ignored - missing jotdoc or markdown', {
                hasJotdoc: !!currentJotdocId,
                hasMarkdown: !!currentMarkdown,
              });
              return;
            }

            const currentSectionContent = sectionContentRef.current.get(sectionId);
            // Allow empty strings - only skip if content is undefined/null (not tracked)
            if (currentSectionContent === undefined || currentSectionContent === null) {
              console.log('[JotDoc] Blur ignored - no section content tracked', { sectionId });
              return;
            }

            // Get original section content to check if it changed
            const originalSections = parseMarkdownH2Sections(currentMarkdown);
            const originalSection = originalSections.find(s => s.id === sectionId);

            // Only save if content actually changed
            if (originalSection && originalSection.text.trim() === currentSectionContent.trim()) {
              console.log('[JotDoc] Blur ignored - no content changes', { sectionId });
              return;
            }

            console.log('[JotDoc] Saving section on blur', {
              sectionId,
              jotdocId: currentJotdocId,
              originalLength: originalSection?.text.length || 0,
              newLength: currentSectionContent.length,
            });

            // Save the section (optimistic update happens in mutation)
            await saveSectionRef.current({
              jotdocId: currentJotdocId,
              originalMarkdown: currentMarkdown,
              sectionId,
              newSectionContent: currentSectionContent,
            });
          },
          onDelete: async () => {
            console.log('[JotDoc] handleSectionDelete called', { sectionId });

            const currentJotdocId = jotdocIdRef.current;
            // Get current markdown from cache (source of truth) via ref
            const currentMarkdown = currentMarkdownRef.current;
            if (!currentJotdocId || !currentMarkdown) {
              console.log('[JotDoc] Delete ignored - missing jotdoc or markdown', {
                hasJotdoc: !!currentJotdocId,
                hasMarkdown: !!currentMarkdown,
              });
              return;
            }

            // Remove the section from markdown
            const updatedMarkdown = removeSectionFromMarkdown(currentMarkdown, sectionId);

            console.log('[JotDoc] Deleting section', {
              sectionId,
              jotdocId: currentJotdocId,
              originalLength: currentMarkdown.length,
              updatedLength: updatedMarkdown.length,
            });

            // Remove section from content ref
            sectionContentRef.current.delete(sectionId);

            // Save the updated markdown (optimistic update happens in mutation)
            await saveMarkdownRef.current({
              jotdocId: currentJotdocId,
              updatedMarkdown,
            });
          },
          onHeadingChange: async (newHeading: string) => {
            console.log('[JotDoc] handleHeadingChange called', { sectionId, newHeading });

            const currentJotdocId = jotdocIdRef.current;
            // Get current markdown from cache (source of truth) via ref
            const currentMarkdown = currentMarkdownRef.current;
            if (!currentJotdocId || !currentMarkdown) {
              console.log('[JotDoc] Heading change ignored - missing jotdoc or markdown', {
                hasJotdoc: !!currentJotdocId,
                hasMarkdown: !!currentMarkdown,
              });
              return;
            }

            // Update the heading in markdown
            const updatedMarkdown = updateSectionHeading(currentMarkdown, sectionId, newHeading);

            console.log('[JotDoc] Updating heading', {
              sectionId,
              jotdocId: currentJotdocId,
              newHeading,
              originalLength: currentMarkdown.length,
              updatedLength: updatedMarkdown.length,
            });

            // Save the updated markdown (optimistic update happens in mutation)
            await saveMarkdownRef.current({
              jotdocId: currentJotdocId,
              updatedMarkdown,
            });
          },
          onAddSectionBelow: async () => {
            console.log('[JotDoc] handleAddSectionBelow called', { sectionId });

            const currentJotdocId = jotdocIdRef.current;
            // Get current markdown from cache (source of truth) via ref
            const currentMarkdown = currentMarkdownRef.current;
            if (!currentJotdocId || !currentMarkdown) {
              console.log('[JotDoc] Add section ignored - missing jotdoc or markdown', {
                hasJotdoc: !!currentJotdocId,
                hasMarkdown: !!currentMarkdown,
              });
              return;
            }

            // Insert new section after this section
            const updatedMarkdown = insertSectionAfter(
              currentMarkdown,
              sectionId,
              'New Note Section',
            );

            console.log('[JotDoc] Adding section below', {
              sectionId,
              jotdocId: currentJotdocId,
              originalLength: currentMarkdown.length,
              updatedLength: updatedMarkdown.length,
            });

            // Optimistically update cache immediately for instant UI feedback
            const patchResult = dispatch(
              jotdocsApi.util.updateQueryData(
                'getV2ProviderJotdocsByJotdocId',
                { jotdocId: currentJotdocId },
                draft => {
                  if (draft.jotdoc) {
                    draft.jotdoc.content = updatedMarkdown;
                  }
                },
              ),
            ) as unknown as { undo: () => void };

            // Save the updated markdown
            await saveMarkdownRef.current({
              jotdocId: currentJotdocId,
              updatedMarkdown,
              onError: () => {
                // Rollback optimistic update on error
                patchResult.undo();
              },
            });
          },
        });
      }
      return sectionCallbacksRef.current.get(sectionId)!;
    },
    [dispatch],
  ); // Include dispatch for optimistic updates

  // Clean up callbacks for sections that no longer exist
  React.useEffect(() => {
    const existingSectionIds = new Set(sections.map(s => s.id));
    for (const [sectionId] of sectionCallbacksRef.current) {
      if (!existingSectionIds.has(sectionId)) {
        sectionCallbacksRef.current.delete(sectionId);
      }
    }
  }, [sections]);

  // Handle appending text to a section
  const handleAppendText = useCallback(
    async (sectionId: string, newFullContent: string) => {
      // Use the standardized hook for optimistic save
      await saveSectionWithOptimistic(sectionId, newFullContent);
    },
    [saveSectionWithOptimistic],
  );

  // Get active section for drag overlay
  const activeSection = activeSectionId ? sections.find(s => s.id === activeSectionId) : null;

  // Render sections
  return (
    <>
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragStart={handleSectionDragStart}
        onDragEnd={handleSectionDragEnd}
        measuring={{
          droppable: {
            strategy: MeasuringStrategy.BeforeDragging,
          },
        }}
      >
        <SortableContext items={sections.map(s => s.id)} strategy={verticalListSortingStrategy}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {sections.map((section, index) => {
              // Use tracked content if available, otherwise use parsed content
              const sectionContent = sectionContentRef.current.get(section.id) ?? section.text;
              const callbacks = getSectionCallbacks(section.id);

              const isSelected = selectedSectionIds.includes(section.id);

              return (
                <JotDocSection
                  key={section.id}
                  heading={section.title}
                  markdown={sectionContent}
                  sectionId={section.id}
                  sectionOrder={index}
                  onMarkdownChange={callbacks.onMarkdownChange}
                  onBlur={callbacks.onBlur}
                  onDelete={callbacks.onDelete}
                  onHeadingChange={callbacks.onHeadingChange}
                  onAddSectionBelow={callbacks.onAddSectionBelow}
                  encounterId={encounterId}
                  isDragActive={activeSectionId !== null}
                  jotdocId={jotdoc?.jotdoc_id}
                  isSelected={isSelected}
                  onToggleSelection={
                    onToggleSectionSelection
                      ? () => onToggleSectionSelection(section.id)
                      : undefined
                  }
                  onAppendText={handleAppendText}
                  isScrolling={isScrolling}
                  troubleWords={troubleWords}
                  misspelledWords={misspelledWords}
                  onWordClick={onWordClick}
                  editable={!streamingIndicator}
                  onEditorReady={editor => {
                    if (editor) {
                      blockNoteEditorsRef.current.set(section.id, editor);
                    } else {
                      blockNoteEditorsRef.current.delete(section.id);
                    }
                    // Notify parent of editor changes
                    if (onEditorsReady) {
                      const currentMap = new Map(blockNoteEditorsRef.current);
                      onEditorsReady(currentMap);
                    }
                  }}
                />
              );
            })}
            {/* Streaming indicator appears after the last section */}
            {streamingIndicator}
            {/* 10vh spacer at the bottom of all sections */}
            <Box sx={{ height: '10vh' }} />
          </Box>
        </SortableContext>

        {/* DragOverlay - shows dragged section preview */}
        <DragOverlay
          dropAnimation={
            validDrop
              ? null // No animation on successful drop to prevent flash
              : {
                  duration: 500,
                  easing: 'cubic-bezier(0.18, 0.67, 0.6, 1.22)',
                }
          }
        >
          {activeSection ? (
            <Box
              sx={{
                opacity: 0.9,
                border: 2,
                borderColor: 'primary.main',
                borderRadius: '0.75rem',
                bgcolor: 'backgroundColors.primary',
                p: 2,
                boxShadow: 8,
                cursor: 'grabbing',
                minWidth: '300px',
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 'bold',
                  color: 'textColors.darkText',
                  mb: 1,
                }}
              >
                {activeSection.title?.replace(/\s*\([^)]*\)/g, '')}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: 'text.secondary',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                }}
              >
                {activeSection.text.substring(0, 100)}...
              </Typography>
            </Box>
          ) : null}
        </DragOverlay>
      </DndContext>
    </>
  );
};

export default JotDocNoteSectionView;
